import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Modal,
  IconButton,
  Stack,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Fab,
  Tooltip,
} from "@mui/material";
import {
  PlayArrow,
  Pause,
  FastForward,
  FastRewind,
  Fullscreen,
  FullscreenExit,
  VideoLibrary,
  Close,
} from "@mui/icons-material";
import { ChevronRight, XIcon } from "lucide-react";
import { useTheme } from "../../hooks/useTheme";

const VideoPlayerModal = ({
  open,
  onClose,
  videoUrls = [],
  questions = [],
  durations = [],
  followUps = [],
}) => {
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [currentFollowUpIndex, setCurrentFollowUpIndex] = useState(null); // null = main question, number = follow-up index
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cuePoints, setCuePoints] = useState([]);
  const [expandedQuestions, setExpandedQuestions] = useState({});

  const {theme} = useTheme();

  const videoRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (durations && durations[currentVideoIndex]) {
      setDuration(durations[currentVideoIndex]);
    } else {
      setDuration(0); 
    }
  }, [currentVideoIndex, durations]);

  useEffect(() => {
    if (videoUrls.length > 0) {
      let totalDuration = 0;
      const points = videoUrls.map((_, index) => {
        const point = totalDuration;
        totalDuration += 60;
        return {
          time: point,
          questionIndex: index,
          label: `Q${index + 1}`,
        };
      });
      setCuePoints(points);
    }
  }, [videoUrls]);

  useEffect(() => {
    if (!videoRef.current) {
      return;
    }

    const video = videoRef.current;

    const loadVideo = () => {
      setLoading(false);
      
      // Only set duration from video if backend didn't provide it
      if (!duration || !isFinite(duration) || duration === 0) {
        console.log('No backend duration, using video duration');
        if (isFinite(video.duration) && video.duration > 0) {
          setDuration(video.duration);
        } 
      }

      if (currentVideoIndex > 0 && cuePoints.length > currentVideoIndex) {
        const updatedCuePoints = [...cuePoints];
        updatedCuePoints[currentVideoIndex].time =
          updatedCuePoints[currentVideoIndex - 1].time +
          video.duration;
        setCuePoints(updatedCuePoints);
      }
    };

    const handleTimeUpdate = () => {
      if (video) {
        setCurrentTime(video.currentTime);
      }
    };

    const handleDurationChange = () => {
      if (!duration || !isFinite(duration) || duration === 0) {
        if (isFinite(video.duration) && video.duration > 0) {
          setDuration(video.duration);
        }
      }
    };

    const handleCanPlay = () => {     
      if (!duration || !isFinite(duration) || duration === 0) {
        if (isFinite(video.duration) && video.duration > 0) {
          setDuration(video.duration);
        }
      }
    };

    const handlePlay = () => {
      console.log(' Video play event');
    };

    const handlePause = () => {
      console.log(' Video pause event');
    };

    // Add event listeners
    console.log('Adding event listeners...');
    video.addEventListener("loadedmetadata", loadVideo);
    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("durationchange", handleDurationChange);
    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);

    if (video.readyState >= 2) {
      handleTimeUpdate();
      if (isFinite(video.duration) && video.duration > 0) {
        loadVideo();
      }
    }

    return () => {
      if (video) {
        video.removeEventListener("loadedmetadata", loadVideo);
        video.removeEventListener("timeupdate", handleTimeUpdate);
        video.removeEventListener("durationchange", handleDurationChange);
        video.removeEventListener("canplay", handleCanPlay);
        video.removeEventListener("play", handlePlay);
        video.removeEventListener("pause", handlePause);
      }
    };
  }, [open, currentVideoIndex, cuePoints, duration, videoRef.current]);

  useEffect(() => {
    if (videoRef.current) {
      setLoading(true);
      setCurrentTime(0);
      videoRef.current.currentTime = 0;
      if (isPlaying) {
        videoRef.current
          .play()
          .catch((e) => console.error("Playback failed:", e));
      }
    }
  }, [currentVideoIndex, currentFollowUpIndex, isPlaying]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setCurrentVideoIndex(0);
      setCurrentTime(0);
      setIsPlaying(false);
      
      if (durations && durations[0]) {
        setDuration(durations[0]);
      } else {
        setDuration(0);
      }
    }
  }, [open, durations]);

  const handlePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current
          .play()
          .catch((e) => console.error("Playback failed:", e));
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleVideoEnd = () => {
    if (currentVideoIndex < videoUrls.length - 1) {
      setCurrentVideoIndex(currentVideoIndex + 1);
    } else {
      setIsPlaying(false);
    }
  };

  const handleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await containerRef.current.requestFullscreen();
        setFullscreen(true);
      } else {
        await document.exitFullscreen();
        setFullscreen(false);
      }
    } catch (error) {
      console.error('Fullscreen toggle error:', error);
      setFullscreen(!!document.fullscreenElement);
    }
  };

  const skipForward = () => {
    if (videoRef.current) {
      const video = videoRef.current;
      
      if (video.readyState >= 2) {
        const newTime = video.currentTime + 15;
        const maxTime = (duration && isFinite(duration)) 
          ? duration 
          : (video.duration && isFinite(video.duration))
            ? video.duration
            : newTime;
        video.currentTime = Math.min(maxTime, newTime);
      } else {
        console.log('Video not ready, readyState:', video.readyState);
      }
    }
  };

  const skipBackward = () => {
    if (videoRef.current) {
      const video = videoRef.current;
      
      if (video.readyState >= 2) {
        const newTime = Math.max(video.currentTime - 15, 0);
        console.log('New time:', newTime);
        video.currentTime = newTime;
      } else {
        console.log('Video not ready, readyState:', video.readyState);
      }
    }
  };

  const handleSeek = (e) => {
    const newTime = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || !isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get current video URL based on main question or follow-up selection
  const getCurrentVideoUrl = () => {
    if (currentFollowUpIndex !== null) {
      const fu = followUps[currentVideoIndex]?.[currentFollowUpIndex];
      return fu?.user_video_response || '';
    }
    return videoUrls[currentVideoIndex] || '';
  };

  // Get current question text
  const getCurrentQuestion = () => {
    if (currentFollowUpIndex !== null) {
      const fu = followUps[currentVideoIndex]?.[currentFollowUpIndex];
      return fu?.question || 'No question available';
    }
    return questions[currentVideoIndex] || 'No question available';
  };

  // Get current question label
  const getCurrentLabel = () => {
    if (currentFollowUpIndex !== null) {
      return `FOLLOW-UP ${currentFollowUpIndex + 1}`;
    }
    return `QUESTION ${currentVideoIndex + 1}`;
  };

  // Hide sidebar when there is only one video and no follow-up clips (e.g. LA full-session recording)
  const hideSidebar = videoUrls.length <= 1 && !followUps.some((q) => (q || []).some((f) => f?.user_video_response));

  // Toggle question expansion in sidebar
  const toggleExpand = (index) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Jump to main question
  const jumpToQuestion = (index) => {
    setCurrentVideoIndex(index);
    setCurrentFollowUpIndex(null);
    setCurrentTime(0);
  };

  // Jump to follow-up
  const jumpToFollowUp = (questionIndex, followUpIndex) => {
    setCurrentVideoIndex(questionIndex);
    setCurrentFollowUpIndex(followUpIndex);
    setCurrentTime(0);
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="video-library-modal">
      <Box
        ref={containerRef}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: fullscreen ? "100%" : "80%",
          height: fullscreen ? "100%" : "80%",
          maxWidth: fullscreen ? "none" : 1200,
          maxHeight: fullscreen ? "none" : 800,
          bgcolor: `${theme === 'dark' ? "#111827" : ""}`,
          boxShadow: 24,
          borderRadius: fullscreen ? 0 : 2,
          overflow: "hidden",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {/* Video Player Section */}
        <Box
          sx={{
            flex: "1 1 70%",
            display: "flex",
            flexDirection: "column",
            bgcolor: `${theme === 'dark' ? "#111827" : ""}`,
            position: "relative",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {/* Video */}
          <Box
            sx={{
              position: "relative",
              flex: "1 1 auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: `${theme === 'dark' ? "#000" : "#FAFAFA"}`,
              overflow: "hidden",
              pt: 6, // Add top padding to push video down slightly
            }}
          >
            <Box sx={{ position: "absolute", top: 10, right: 10, zIndex: 2 }}>
              <IconButton onClick={onClose} sx={{ color: "white" }}>
                <XIcon className="text-textSecondary" />
              </IconButton>
            </Box>

            {getCurrentVideoUrl() && (
              <video
                ref={videoRef}
                src={getCurrentVideoUrl()}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
                onEnded={handleVideoEnd}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
              />
            )}

            {/* Question overlay — hidden for single-video (no sidebar) case */}
            {!hideSidebar && <Box
              sx={{
                position: "absolute",
                top: 10,
                left: 10,
                maxWidth: "80%",
                padding: 1.5,
                bgcolor: `${theme === 'dark' ? "#000" : "#FAFAFA"}`,
                borderRadius: 1,
                backdropFilter: "blur(4px)",
                border: "1px solid rgba(255,255,255,0.1)",
                opacity: 0.8,
                transition: "opacity 0.3s",
                "&:hover": { opacity: 1 },
              }}
            >
              <Typography
                variant="subtitle2"
                color={theme === 'dark' ? "rgba(255,255,255,0.8)" : ""}
                sx={{ mb: 0.5, fontSize: "0.8rem" }}
              >
                {getCurrentLabel()}
              </Typography>
              <Typography
                variant="body1"
                color={theme === 'dark' ? "white" : "#525252"}
                sx={{ fontWeight: 500, fontSize: "0.9rem" }}
              >
                {getCurrentQuestion()}
              </Typography>
            </Box>}
          </Box>

          {/* Video Controls */}
          <Paper
            elevation={3}
            sx={{
              p: 2,
              bgcolor: `${theme === 'dark' ? "#000" : "#FAFAFA"}`,
              borderTop: "1px solid rgba(255,255,255,0.05)",
              display: "flex",
              flexDirection: "column",
              flexShrink: 0,
              minHeight: duration && isFinite(duration) && duration > 0 ? 100 : 56,
            }}
          >
            {/* Show progress bar only when duration is available */}
            {duration && isFinite(duration) && duration > 0 ? (
              <>
                {/* Progress Bar */}
                <input
                  type="range"
                  min="0"
                  max={duration}
                  value={currentTime}
                  onChange={handleSeek}
                  style={{
                    width: "100%",
                    marginBottom: "12px",
                    cursor: "pointer",
                    accentColor: "#4f46e5",
                  }}
                />
                
                {/* Time Display and Controls */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <IconButton
                      onClick={skipBackward}
                      size="small"
                      sx={{ color: `${theme === 'dark' ? "white" : "#525252"}` }}
                    >
                      <FastRewind />
                    </IconButton>
                    <IconButton
                      onClick={handlePlay}
                      size="small"
                      sx={{ color: `${theme === 'dark' ? "white" : "#525252"}` }}
                    >
                      {isPlaying ? <Pause /> : <PlayArrow />}
                    </IconButton>
                    <IconButton
                      onClick={skipForward}
                      size="small"
                      sx={{ color: `${theme === 'dark' ? "white" : "#525252"}` }}
                    >
                      <FastForward />
                    </IconButton>
                    
                    {/* Time Display */}
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme === 'dark' ? "white" : "#525252",
                        fontSize: "0.85rem",
                        ml: 1,
                      }}
                    >
                      {formatTime(currentTime)} / {formatTime(duration)}
                    </Typography>
                  </Stack>

                  <IconButton
                    onClick={handleFullscreen}
                    size="small"
                    sx={{ color: `${theme === 'dark' ? "white" : "#525252"}` }}
                  >
                    {fullscreen ? <FullscreenExit /> : <Fullscreen />}
                  </IconButton>
                </Stack>
              </>
            ) : (
              /* Loading state - no progress bar */
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={1} alignItems="center">
                  <IconButton
                    onClick={skipBackward}
                    size="small"
                    sx={{ color: `${theme === 'dark' ? "white" : "#525252"}` }}
                  >
                    <FastRewind />
                  </IconButton>
                  <IconButton
                    onClick={handlePlay}
                    size="small"
                    sx={{ color: `${theme === 'dark' ? "white" : "#525252"}` }}
                  >
                    {isPlaying ? <Pause /> : <PlayArrow />}
                  </IconButton>
                  <IconButton
                    onClick={skipForward}
                    size="small"
                    sx={{ color: `${theme === 'dark' ? "white" : "#525252"}` }}
                  >
                    <FastForward />
                  </IconButton>
                  
                  {/* Loading text */}
                  {/* <Typography
                    variant="body2"
                    sx={{
                      color: theme === 'dark' ? "white" : "#525252",
                      fontSize: "0.85rem",
                      ml: 1,
                    }}
                  >
                    Loading...
                  </Typography> */}
                </Stack>

                <IconButton
                  onClick={handleFullscreen}
                  size="small"
                  sx={{ color: `${theme === 'dark' ? "white" : "#525252"}` }}
                >
                  {fullscreen ? <FullscreenExit /> : <Fullscreen />}
                </IconButton>
              </Stack>
            )}
          </Paper>
        </Box>

        {/* Video List Section — hidden for single-video case (e.g. full-session LA recording) */}
        {!hideSidebar && <Box
          sx={{
            width: { xs: "100%", md: 280 },
            height: { xs: "auto", md: "100%" },
            bgcolor: `${theme === 'dark' ? "#000" : "#FAFAFA"}`,
            overflowY: "auto",
            borderLeft: "1px solid rgba(255,255,255,0.05)",
            flexShrink: 0,
          }}
        >
          <Box sx={{ p: 1.5, borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
            <Typography
              variant="h6"
              color={theme === 'dark' ? "white":"#525252"}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontSize: "1rem",
              }}
            >
              Video Responses
            </Typography>
            <Typography
              variant="body2"
              color={theme === 'dark' ? "rgba(255,255,255,0.7)":"#525252"}
              sx={{ mt: 0.5, fontSize: "0.75rem" }}
            >
              Click on a question to jump to that video
            </Typography>
          </Box>

          <List disablePadding dense>
            {videoUrls.map((url, index) => {
              const questionFollowUps = followUps[index] || [];
              const followUpsWithVideo = questionFollowUps.reduce((acc, fu, idx) => {
                if (fu?.user_video_response) acc.push({ ...fu, originalIndex: idx });
                return acc;
              }, []);
              const isExpanded = expandedQuestions[index];
              const isCurrentQuestion = currentVideoIndex === index && currentFollowUpIndex === null;

              return (
                <React.Fragment key={index}>
                  {/* Question Container - wraps main question and follow-ups */}
                  <Box
                    sx={{
                      bgcolor: isExpanded && followUpsWithVideo.length > 0
                        ? (theme === 'dark' ? 'rgba(79,70,229,0.05)' : 'rgba(79,70,229,0.03)')
                        : 'transparent',
                      borderRadius: isExpanded ? 1 : 0,
                      mb: isExpanded ? 0.5 : 0,
                    }}
                  >
                  {/* Main Question */}
                  <ListItem
                    sx={{
                      cursor: "pointer",
                      bgcolor: isCurrentQuestion ? "rgba(79,70,229,0.1)" : "transparent",
                      borderLeft: isCurrentQuestion ? "4px solid #4f46e5" : "4px solid transparent",
                      py: 1,
                      "&:hover": { bgcolor: "rgba(79,70,229,0.08)" },
                    }}
                    onClick={() => jumpToQuestion(index)}
                  >
                    <ListItemText
                      primary={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box
                            sx={{
                              width: 22,
                              height: 22,
                              borderRadius: "50%",
                              bgcolor: isCurrentQuestion ? "#4f46e5" : theme === 'dark' ? "rgba(255,255,255,0.1)" : "#525252",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "white",
                              fontWeight: "bold",
                              fontSize: "0.7rem",
                            }}
                          >
                            {index + 1}
                          </Box>
                          <Typography
                            sx={{
                              fontWeight: isCurrentQuestion ? "bold" : "normal",
                              color: isCurrentQuestion ? "#4f46e5" : theme === 'dark' ? "white" : "#525252",
                              fontSize: "0.85rem",
                            }}
                          >
                            Question {index + 1}
                          </Typography>
                          {followUpsWithVideo.length > 0 && (
                            <Typography
                              sx={{
                                fontSize: "0.65rem",
                                color: theme === 'dark' ? "rgba(255,255,255,0.5)" : "#888",
                                ml: 0.5,
                              }}
                            >
                              ({followUpsWithVideo.length} follow-up{followUpsWithVideo.length > 1 ? 's' : ''})
                            </Typography>
                          )}
                        </Stack>
                      }
                      secondary={
                        <Typography
                          variant="body2"
                          color={theme === 'dark' ? "rgba(255,255,255,0.7)" : "#525252"}
                          sx={{
                            ml: 4,
                            mt: 0.25,
                            maxWidth: "100%",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontSize: "0.7rem",
                          }}
                        >
                          {questions[index] || "No question available"}
                        </Typography>
                      }
                    />
                    {followUpsWithVideo.length > 0 && (
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpand(index);
                        }}
                        sx={{
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          p: 0.5,
                        }}
                      >
                        <ChevronRight
                          size={16}
                          style={{
                            color: isCurrentQuestion ? "#4f46e5" : theme === 'dark' ? "rgba(255,255,255,0.5)" : "#525252",
                            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s',
                          }}
                        />
                      </Box>
                    )}
                  </ListItem>

                  {/* Follow-ups with branch lines */}
                  {isExpanded && followUpsWithVideo.length > 0 && (
                    <Box sx={{ ml: 3.5, position: 'relative' }}>
                      {followUpsWithVideo.map((fu, fuIdx) => {
                        const isActive = currentVideoIndex === index && currentFollowUpIndex === fu.originalIndex;
                        const isLast = fuIdx === followUpsWithVideo.length - 1;
                        const lineColor = theme === 'dark' ? 'rgba(124,58,237,0.4)' : 'rgba(124,58,237,0.3)';

                        return (
                          <Box
                            key={`fu-${index}-${fuIdx}`}
                            sx={{
                              position: 'relative',
                              '&::before': { content: '""', position: 'absolute', left: 0, top: 0, width: 2, height: isLast ? '50%' : '100%', bgcolor: lineColor },
                              '&::after': { content: '""', position: 'absolute', left: 0, top: '50%', width: 12, height: 2, bgcolor: lineColor },
                            }}
                          >
                            <ListItem
                              sx={{ cursor: "pointer", bgcolor: isActive ? "rgba(124,58,237,0.15)" : "transparent", py: 0.75, pl: 2.5, "&:hover": { bgcolor: "rgba(124,58,237,0.08)" } }}
                              onClick={() => jumpToFollowUp(index, fu.originalIndex)}
                            >
                              <ListItemText
                                primary={
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <Box sx={{ width: 22, height: 22, minWidth: 22, borderRadius: "50%", flexShrink: 0, bgcolor: isActive ? "#7c3aed" : theme === 'dark' ? "rgba(124,58,237,0.15)" : "#f3e8ff", display: "flex", alignItems: "center", justifyContent: "center", color: isActive ? "white" : theme === 'dark' ? "#a78bfa" : "#7c3aed", fontWeight: "bold", fontSize: "0.7rem", border: isActive ? "none" : `1.5px solid ${lineColor}` }}>
                                      {fuIdx + 1}
                                    </Box>
                                    <Typography sx={{ fontWeight: isActive ? 600 : 500, color: isActive ? "#7c3aed" : theme === 'dark' ? "rgba(255,255,255,0.85)" : "#525252", fontSize: "0.8rem" }}>
                                      Follow-up {fuIdx + 1}
                                    </Typography>
                                  </Stack>
                                }
                                secondary={
                                  <Typography variant="body2" color={theme === 'dark' ? "rgba(255,255,255,0.6)" : "#666"} sx={{ ml: 4, mt: 0.25, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", fontSize: "0.7rem" }}>
                                    {fu.question || "No question available"}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                  </Box>
                  <Divider sx={{ bgcolor: "rgba(255,255,255,0.05)" }} />
                </React.Fragment>
              );
            })}
          </List>
        </Box>}
      </Box>
    </Modal>
  );
};

const VideoLibraryButton = ({ onClick }) => {
  return (
    <Tooltip title="Video Library">
      <Fab
        color="primary"
        aria-label="video library"
        onClick={onClick}
        sx={{
          position: "fixed",
          bottom: 32,
          right: 102,
          bgcolor: "#4f46e5",
          "&:hover": { bgcolor: "#4338ca" },
          boxShadow: "0 4px 15px rgba(79,70,229,0.4)",
          displayPrint: "none",
        }}
      >
        <VideoLibrary />
      </Fab>
    </Tooltip>
  );
};

export { VideoPlayerModal, VideoLibraryButton };